import { useState } from "react";
import { History, MessageSquarePlus, MessagesSquare, Trash2, X } from "lucide-react";
import { useCan } from "@/app/auth";
import { cn } from "@/lib/cn";
import { relative } from "@/lib/format";
import { BetaBadge, Drawer, EmptyState, ErrorState, IconButton, SkeletonRows, useConfirm, useToast } from "@/components/ui";
import { useAiStatus, useAssistantThreads, useDeleteThread } from "./api";
import { AssistantChat } from "./assistant-chat";
import { AiAvatar } from "./parts";

// AI assistant drawer opened from the top bar: conversation, history of the member's threads in the current client,
// monthly quota state.

function QuotaLine() {
  const canUse = useCan("ai.use");
  const status = useAiStatus(canUse);
  if (!status.data) return null;
  const { org, member } = status.data;
  const memberPart = member.limit !== null && member.pct !== null ? `, vous : ${Math.round(member.pct)} %` : "";
  const tone = org.pct >= 100 || (member.pct ?? 0) >= 100 ? "text-danger" : org.pct >= 80 || (member.pct ?? 0) >= 80 ? "text-warning" : "text-muted";
  return (
    <p className={cn("text-[11.5px]", tone)}>
      Quota IA du mois : {Math.round(org.pct)} % utilisé{memberPart}
    </p>
  );
}

function ThreadsList({ activeId, onOpen }: { activeId: string | null; onOpen: (id: string | null) => void }) {
  const threads = useAssistantThreads();
  const remove = useDeleteThread();
  const confirm = useConfirm();
  const toast = useToast();
  if (threads.isLoading) return <SkeletonRows rows={6} className="p-4" />;
  if (threads.error) return <ErrorState error={threads.error} onRetry={() => void threads.refetch()} />;
  const items = threads.data?.items ?? [];
  if (items.length === 0) {
    return <EmptyState compact icon={<MessagesSquare className="size-5" />} title="Aucune discussion" description="Vos échanges avec l'assistant apparaîtront ici, pour les reprendre plus tard." />;
  }
  return (
    <ul className="divide-y divide-line">
      {items.map((t) => (
        <li key={t.id} className={cn("group flex items-start gap-2 px-4 py-3 sm:px-5", t.id === activeId && "bg-primary/[0.07]")}>
          <button type="button" onClick={() => onOpen(t.id)} className="min-w-0 flex-1 text-left">
            <p className="truncate text-[13.5px] font-medium text-ink">{t.title}</p>
            {t.preview ? <p className="mt-0.5 line-clamp-2 text-[12.5px] text-muted">{t.preview}</p> : null}
            <p className="mt-1 text-[11px] text-faint">{relative(t.lastMessageAt)}</p>
          </button>
          <IconButton
            label="Supprimer la discussion"
            size="sm"
            variant="danger"
            onClick={async () => {
              if (!(await confirm({ title: "Supprimer cette discussion ?", description: "L'historique de cet échange sera définitivement supprimé.", confirmLabel: "Supprimer", danger: true }))) return;
              try {
                await remove.mutateAsync(t.id);
                if (t.id === activeId) onOpen(null);
                toast.success("Discussion supprimée");
              } catch (err) {
                toast.fromError(err);
              }
            }}
          >
            <Trash2 className="size-4" />
          </IconButton>
        </li>
      ))}
    </ul>
  );
}

export function AssistantDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [view, setView] = useState<"chat" | "history">("chat");
  return (
    <Drawer open={open} onClose={onClose} hideHeader width="w-full sm:w-[480px]">
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-3.5 sm:px-5">
          <AiAvatar />
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ink">
              Assistant IA <BetaBadge />
            </h2>
            <QuotaLine />
          </div>
          <IconButton label={view === "history" ? "Revenir à la discussion" : "Historique des discussions"} size="sm" onClick={() => setView(view === "history" ? "chat" : "history")}>
            <History className="size-4" />
          </IconButton>
          <IconButton
            label="Nouvelle discussion"
            size="sm"
            onClick={() => {
              setThreadId(null);
              setView("chat");
            }}
          >
            <MessageSquarePlus className="size-4" />
          </IconButton>
          <IconButton label="Fermer" size="sm" onClick={onClose}>
            <X className="size-4" />
          </IconButton>
        </div>
        {view === "history" ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ThreadsList
              activeId={threadId}
              onOpen={(id) => {
                setThreadId(id);
                setView("chat");
              }}
            />
          </div>
        ) : (
          <AssistantChat variant="drawer" threadId={threadId} onThreadChange={setThreadId} className="flex-1" />
        )}
      </div>
    </Drawer>
  );
}
